import React, { useRef, useLayoutEffect } from 'react';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_dataviz from "@amcharts/amcharts4/themes/dataviz";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_dataviz);
am4core.useTheme(am4themes_animated);

const CATEGORY_ORDER = ["NOT AVAILABLE", "INACTIVE", "ACTIVE"];

const COLOR_MAP = {
  ACTIVE: am4core.color("#4CAF50"),
  INACTIVE: am4core.color("#F44336"),
  "NOT AVAILABLE": am4core.color("#FF9800"),
};


const DeviceTimelineGraph = ({ data }) => {
  const chartRef = useRef(null);

  useLayoutEffect(() => {
    if (!data || !data.length) {
      if (chartRef.current) chartRef.current.dispose();
      return;
    }

    if (chartRef.current) {
      chartRef.current.dispose();
    }

    // -------------------- PROCESS DATA --------------------
    let processedData = [];
    const sorted = [...data].sort((a, b) => new Date(a.start) - new Date(b.start));

    let previousEnd = null;

    sorted.forEach((record) => {
      const start = new Date(record.start);
      const end = new Date(record.end);
      const status = record.status.toUpperCase();

      // Fill NOT AVAILABLE gap
      if (previousEnd && start > previousEnd) {
        if (start - previousEnd > 1000) {
          processedData.push({
            openDateX: previousEnd,
            dateX: start,
            category: "NOT AVAILABLE",
            startTime: previousEnd,
            endTime: start,
          });
        }
      }

      processedData.push({
        openDateX: start,
        dateX: end,
        category: status,
        startTime: start,
        endTime: end,
      });

      previousEnd = end;
    });

    // -------------------- CREATE CHART --------------------
    let chart = am4core.create("statusChart", am4charts.XYChart);
    chartRef.current = chart;
    chart.data = processedData;

    // -------------------- AXES --------------------
    let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.baseInterval = { timeUnit: "minute", count: 1 };
    dateAxis.renderer.labels.template.rotation = -45;
    dateAxis.renderer.minGridDistance = 70;

    dateAxis.renderer.line.stroke = am4core.color("#000");
    dateAxis.renderer.line.strokeWidth = 2;

    dateAxis.mouseWheelZoomEnabled = true;
    chart.mouseWheelBehavior = "zoomX";

    let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "category";
    categoryAxis.renderer.inversed = true;

    categoryAxis.dataItems.template.events.on("ready", (ev) => {
      ev.target.axis.categories = CATEGORY_ORDER;
    });

    // -------------------- SERIES --------------------
    let series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.openDateX = "openDateX";
    series.dataFields.dateX = "dateX";
    series.dataFields.categoryY = "category";

    series.dataFields.startTime = "startTime";
    series.dataFields.endTime = "endTime";

    // Fix hit-detection so tooltips ALWAYS work
    series.columns.template.interactionsEnabled = true;
    series.columns.template.minHeight = 8;

    // IMPORTANT: Don't set tooltipText - it conflicts with tooltipHTML
    series.columns.template.tooltipText = undefined;

    series.columns.template.column.width = am4core.percent(100);
    series.columns.template.column.height = am4core.percent(90);
    series.columns.template.column.strokeWidth = 0;

    series.columns.template.column.adapter.add("fill", (_, target) => {
      return COLOR_MAP[target.dataItem.dataContext.category];
    });

    // -------------------- TOOLTIP (FIXED) --------------------
    // Enable HTML tooltip
    series.columns.template.tooltipHTML = "";
    series.tooltip.pointerOrientation = "vertical";

    // Make sure tooltip background is visible
    series.tooltip.getFillFromObject = false;
    series.tooltip.background.fill = am4core.color("#fff");
    series.tooltip.background.strokeWidth = 0;

    // THE FIX: Adapter should be on columns.template, not series
    series.columns.template.adapter.add("tooltipHTML", (html, target) => {
      const ctx = target?.dataItem?.dataContext;
      if (!ctx) return "<div>No data</div>";

      const start = new Date(ctx.startTime);
      const end = new Date(ctx.endTime);

      const diff = Math.round((end - start) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;

      const duration = (h ? h + "h " : "") + (m ? m + "m " : "") + s + "s";

      let borderColor = "#666";
      let durationText = `Duration: ${duration}`;

      if (ctx.category === "ACTIVE") borderColor = "#4CAF50";
      else if (ctx.category === "INACTIVE") borderColor = "#F44336";
      else if (ctx.category === "NOT AVAILABLE") {
        borderColor = "#FF9800";
        durationText = "Data not available during this period";
      }

      return `
                <div style="
                    padding: 10px;
                    background: white;
                    border: 2px solid ${borderColor};
                    border-radius: 6px;
                    color: black;
                    font-size: 12px;
                ">
                    <strong style="color:${borderColor};">
                        Status: ${ctx.category}
                    </strong><br>
                    Start: ${start.toLocaleString()}<br>
                    End: ${end.toLocaleString()}<br>
                    ${durationText}
                </div>
            `;
    });

    // -------------------- CURSOR --------------------
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "none";
    chart.cursor.xAxis = dateAxis;
    chart.cursor.lineY.disabled = true;
    chart.logo.disabled = true;
    // -------------------- SCROLLBAR --------------------
    chart.scrollbarX = new am4charts.XYChartScrollbar();

    chart.scrollbarX.series.clear();
    chart.scrollbarX.scrollbarChart.series.clear();
    chart.scrollbarX.scrollbarChart.xAxes.clear();
    chart.scrollbarX.scrollbarChart.yAxes.clear();

    chart.scrollbarX.background.fill = am4core.color("#e0e0e0");
    chart.scrollbarX.thumb.background.fill = am4core.color("#999");

    chart.scrollbarX.height = 10;
    chart.scrollbarX.minHeight = 10;

    chart.scrollbarX.scrollbarChart.chartContainer.minHeight = 10;
    chart.scrollbarX.scrollbarChart.chartContainer.maxHeight = 10;
    chart.scrollbarX.scrollbarChart.seriesContainer.height = 0;

    chart.scrollbarX.parent = chart.topAxesContainer;

    return () => chart.dispose();
  }, [data]);

  return (
    <div
      id="statusChart"
      style={{ width: "100%", minHeight: "340px", background: "#fff" }}
    />
  );
};
export default DeviceTimelineGraph
